// hooks/useBackButtonClose.ts
//
// Android's hardware/gesture back button must dismiss the topmost open overlay (modal,
// bottom sheet, dropdown) instead of navigating the app backwards. The web platform has
// no "back button" event, so the standard trick is to let every open overlay occupy a
// history entry of its own:
//
//   open   → history.pushState(marker)
//   back   → popstate fires, we close the overlay and swallow the navigation
//   close  → we unwind the entry we added, so the next back press isn't a dead press
//
// SINGLE SOURCE OF TRUTH for back-button dismissal. Wire overlays up by calling the hook
// rather than adding popstate listeners locally — the module-level stack below is what
// makes nesting (modal → dropdown → wheel picker) unwind in the right order.

import { useEffect, useRef } from 'react';

/** Marker stamped onto our own history entries so we can recognise them later. */
const OVERLAY_STATE_KEY = '__overlayId';

type OverlayEntry = {
  id: number;
  close: () => void;
  /** True once a back press has already consumed this entry's history record. */
  consumed: boolean;
};

/** Currently open overlays, innermost last. A back press closes the last one only. */
const openOverlays: OverlayEntry[] = [];

/**
 * popstate events we caused ourselves (by unwinding entries on close) and must ignore.
 * Counts *events*, not steps: one `history.go(-n)` produces a single popstate.
 */
let popsToSwallow = 0;

/** Entries scheduled to be unwound, batched so one commit costs one `history.go`. */
let pendingUnwinds = 0;
let unwindScheduled = false;

let nextId = 0;
let listening = false;

const overlayIdOfCurrentEntry = (): number | undefined =>
  window.history.state?.[OVERLAY_STATE_KEY];

const handlePopState = () => {
  if (popsToSwallow > 0) {
    popsToSwallow -= 1;
    return;
  }

  const entry = openOverlays[openOverlays.length - 1];
  // Nothing open — a genuine navigation. Leave it to the router.
  if (!entry) return;

  // The browser has already dropped this entry's history record, so closing it must
  // not try to unwind it a second time.
  entry.consumed = true;
  entry.close();
};

/**
 * Attached on first use and deliberately never detached. A pending `popsToSwallow` can
 * outlive the last open overlay (history traversal is async), and detaching would leave
 * that counter set — making the next genuine back press get swallowed by whichever
 * overlay opens next.
 */
const startListening = () => {
  if (listening) return;
  window.addEventListener('popstate', handlePopState);
  listening = true;
};

const scheduleUnwind = () => {
  pendingUnwinds += 1;
  if (unwindScheduled) return;
  unwindScheduled = true;

  queueMicrotask(() => {
    unwindScheduled = false;
    const steps = pendingUnwinds;
    pendingUnwinds = 0;
    if (steps === 0) return;

    // Only unwind while the current entry is still one of ours. If the app navigated
    // on top of it (a link inside the overlay), going back would undo that navigation
    // — better to leave a stale entry behind than to fight the router.
    if (overlayIdOfCurrentEntry() === undefined) return;

    popsToSwallow += 1;
    window.history.go(-steps);
  });
};

/**
 * Makes the back button close an overlay instead of navigating.
 *
 * @param open     Whether the overlay is currently visible.
 * @param onClose  Called when the back button is pressed. Must actually close the
 *                 overlay (flip the same state that drives `open`).
 *
 * Safe to call unconditionally — it does nothing while `open` is false. Nested overlays
 * close one per back press, innermost first.
 */
export const useBackButtonClose = (open: boolean, onClose: () => void) => {
  // Held in a ref so an inline `onClose` arrow doesn't re-run the effect below, which
  // would churn a history entry on every render.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open || typeof window === 'undefined') return undefined;

    nextId += 1;
    const entry: OverlayEntry = {
      id: nextId,
      close: () => onCloseRef.current(),
      consumed: false
    };

    openOverlays.push(entry);
    startListening();

    // Spread the existing state so react-router's `idx` bookkeeping survives. Reusing
    // the current `idx` is what keeps these entries invisible to the router: on popstate
    // it sees delta 0 and an unchanged URL, so it does not treat them as navigations.
    window.history.pushState(
      { ...window.history.state, [OVERLAY_STATE_KEY]: entry.id },
      ''
    );

    return () => {
      const index = openOverlays.indexOf(entry);
      if (index !== -1) openOverlays.splice(index, 1);

      // Closed by a back press — the history record is already gone.
      if (entry.consumed) return;

      // Closed some other way (X button, backdrop, submit): drop the entry we added so
      // the user's next back press navigates instead of doing nothing.
      scheduleUnwind();
    };
  }, [open]);
};
